import { useState } from 'react';
import { useReactiveVar } from '@apollo/client';
import cartItemsVar from '../lib/cartItemsVar';
import { openAppDialog } from './AppDialog';
import BulkActionModal from './BulkActionModal';
import { addToCart } from '../lib/api';

/**
 * @typedef Props
 * @prop {(options: { busy: boolean, inCart: boolean, onClick: () => void }) => void} children
 * @prop {{ product: { name: string } }[]} sales
 */

/**
 * @param {Props} props
 */
export default function AddToCartButton({ children, sales }) {
  const [busy, setBusy] = useState(false);
  const cartItems = useReactiveVar(cartItemsVar);
  const isBulk = sales.length > 1;
  const inCart = sales.every(sale => cartItems.includes(sale.product.name));

  const onClick = async () => {
    setBusy(true);

    if (!isBulk) {
      try {
        await addItem(0);
      } catch (error) {
        console.error(error);
      } finally {
        setBusy(false);
      }
    }
  };

  /**
   * @param {number} index
   */
  const addItem = async index => {
    const cartItems = cartItemsVar();
    const sale = sales[index];
    if (!cartItems.includes(sale.product.name)) {
      try {
        await addToCart(sale);
        cartItemsVar([sale.product.name, ...cartItemsVar()]);
        document.dispatchEvent(
          new CustomEvent('domainSearch.addToCart', {
            detail: sales[0].product.name,
          }),
        );
      } catch (error) {
        openAppDialog('failedToAddDomainToCart', {
          domain: sales[index].product.name,
        });
        setBusy(false);
        throw error;
      }
    }
  };

  return (
    <>
      {children({ busy, inCart, onClick })}
      {isBulk && (
        <BulkActionModal
          title="Adding Domains"
          description="The domains you selected are being added to your shopping cart. This may take a couple minutes."
          itemNamePlural="domains"
          itemCount={sales.length}
          onCancel={progress => {
            document.dispatchEvent(
              new CustomEvent('domainSearch.cancelAddAllToCart', {
                detail: progress,
              }),
            );
          }}
          onProcessIndex={index => addItem(index)}
          onFinish={() => {
            document.dispatchEvent(
              new CustomEvent('domainSearch.addAllToCart', {
                detail: sales.length,
              }),
            );
          }}
          open={busy}
        />
      )}
    </>
  );
}
